import { useWeb3React } from '@web3-react/core'
import { WRAPPED_NATIVE_CURRENCY } from 'constants/tokens'
import { Currency, CurrencyAmount, TradeType } from 'elephantswapv3-sdk-core'
import { DebounceSwapQuoteVariant, useDebounceSwapQuoteFlag } from 'featureFlags/flags/debounceSwapQuote'
import { useMemo } from 'react'
import { InterfaceTrade, TradeState } from 'state/routing/types'
import { useRoutingAPITrade } from 'state/routing/useRoutingAPITrade'
import { useRouterPreference } from 'state/user/hooks'

import useAutoRouterSupported from './useAutoRouterSupported'
import { useClientSideV2Trade } from './useClientSideV2Trade'
import { useClientSideV3Trade } from './useClientSideV3Trade'
import useDebounce from './useDebounce'
import useIsWindowVisible from './useIsWindowVisible'

// Prevents excessive quote requests between keystrokes.
const DEBOUNCE_TIME = 350

// Temporary until we remove the feature flag.
const DEBOUNCE_TIME_INCREASED = 650

/**
 * Returns the best v2+v3 trade for a desired swap.
 * @param tradeType whether the swap is an exact in/out
 * @param amountSpecified the exact amount to swap in/out
 * @param otherCurrency the desired output/payment currency
 */
export function useBestTrade(
  tradeType: TradeType,
  amountSpecified?: CurrencyAmount<Currency>,
  otherCurrency?: Currency
): {
  state: TradeState
  trade?: InterfaceTrade
} {
  const { chainId } = useWeb3React()
  const autoRouterSupported = useAutoRouterSupported()
  const isWindowVisible = useIsWindowVisible()

  const [debouncedAmount, debouncedOtherCurrency] = useDebounce(
    useMemo(() => [amountSpecified, otherCurrency], [amountSpecified, otherCurrency]),
    useDebounceSwapQuoteFlag() === DebounceSwapQuoteVariant.Enabled ? DEBOUNCE_TIME_INCREASED : DEBOUNCE_TIME
  )

  const isAWrapTransaction = useMemo(() => {
    if (!chainId || !amountSpecified || !debouncedOtherCurrency) return false
    const weth = WRAPPED_NATIVE_CURRENCY[chainId]
    return (
      (amountSpecified.currency.isNative && weth?.equals(debouncedOtherCurrency)) ||
      (debouncedOtherCurrency.isNative && weth?.equals(amountSpecified.currency))
    )
  }, [amountSpecified, chainId, debouncedOtherCurrency])

  const shouldGetTrade = !isAWrapTransaction && isWindowVisible

  const [routerPreference] = useRouterPreference()
  const routingAPITrade = useRoutingAPITrade(
    tradeType,
    autoRouterSupported && shouldGetTrade ? debouncedAmount : undefined,
    debouncedOtherCurrency,
    routerPreference
  )

  const isLoading = routingAPITrade.state === TradeState.LOADING
  const useFallback = (!autoRouterSupported || routingAPITrade.state === TradeState.NO_ROUTE_FOUND) && shouldGetTrade

  // only use client side router if routing api trade failed or is not supported
  const bestV3Trade = useClientSideV3Trade(
    tradeType,
    useFallback ? debouncedAmount : undefined,
    useFallback ? debouncedOtherCurrency : undefined
  )

  const bestV2Trade = useClientSideV2Trade(
    tradeType,
    useFallback ? debouncedAmount : undefined,
    useFallback ? debouncedOtherCurrency : undefined
  )

  const fallbackTrade = useMemo(() => {
    if (!useFallback) return { state: TradeState.INVALID, trade: undefined }
    if (!bestV2Trade || !bestV3Trade) {
      return { state: TradeState.LOADING, trade: undefined }
    }
    if (bestV2Trade.trade && bestV3Trade.trade) {
      const useV2 =
        tradeType === TradeType.EXACT_INPUT
          ? bestV2Trade.trade.outputAmount.greaterThan(bestV3Trade.trade.outputAmount)
          : bestV2Trade.trade.inputAmount.lessThan(bestV3Trade.trade.inputAmount)
      return useV2 ? bestV2Trade : bestV3Trade
    }
    if (bestV2Trade.trade) return bestV2Trade
    if (bestV3Trade.trade) return bestV3Trade
    if (bestV2Trade.state === TradeState.VALID) return bestV2Trade
    if (bestV3Trade.state === TradeState.VALID) return bestV3Trade
    if (bestV2Trade.state === TradeState.LOADING || bestV3Trade.state === TradeState.LOADING) {
      return { state: TradeState.LOADING, trade: undefined }
    }
    if (bestV2Trade.state === TradeState.INVALID && bestV3Trade.state === TradeState.INVALID) {
      return { state: TradeState.INVALID, trade: undefined }
    }
    return { state: TradeState.NO_ROUTE_FOUND, trade: undefined }
  }, [bestV2Trade, bestV3Trade, tradeType, useFallback])

  // only return gas estimate from api if routing api trade is used
  return useMemo(
    () => ({
      ...(useFallback ? fallbackTrade : routingAPITrade),
      ...(isLoading ? { state: TradeState.LOADING } : {}),
    }),
    [fallbackTrade, isLoading, routingAPITrade, useFallback]
  )
}
